import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import { FaSpinner } from 'react-icons/fa/index.js';
import CustomDropdown from '../common/CustomDropdown.jsx';
import callOysterApi from '../../utils/call-oyster-api.js';
import { userHasLeadershipAdminRole } from '../../utils/user-leadership.js';
import '../../styles/CreateTaskModal.css';

const LEADERSHIP_HD_TEAM = 'Leadership';

function teamsMatchTeamBulk(a, b) {
    const x = String(a || '')
        .trim()
        .toLowerCase();
    const y = String(b || '')
        .trim()
        .toLowerCase();
    return x.length > 0 && x === y;
}

/** One row per email — API can duplicate rows; Map keys would overwrite and toast would over-count. */
const dedupeUsersByEmail = (list) => {
    const byEmail = new Map();
    list.forEach((u) => {
        const em = String(u.email || '')
            .trim()
            .toLowerCase();
        if (!em) return;
        if (!byEmail.has(em)) byEmail.set(em, u);
    });
    return Array.from(byEmail.values());
};

/** Users shown for "Add by Team" bulk picker; Leadership includes all admin-flag users. */
const usersForTeamBulk = (allUsers, teamName) => {
    const name = typeof teamName === 'string' ? teamName : teamName?.name;
    const t = String(name || '').trim();
    if (!t) return [];
    let filtered;
    if (t.toLowerCase() === LEADERSHIP_HD_TEAM.toLowerCase()) {
        filtered = allUsers.filter(
            (u) =>
                teamsMatchTeamBulk(u.hdTeam, LEADERSHIP_HD_TEAM) ||
                userHasLeadershipAdminRole(u)
        );
    } else {
        filtered = allUsers.filter((u) => teamsMatchTeamBulk(u.hdTeam, t));
    }
    return dedupeUsersByEmail(filtered);
};

const itemTypeNounForCount = (type, count) => {
    if (type === 'rock') {
        return count === 1 ? 'mission' : 'missions';
    }
    return count === 1 ? 'task' : 'tasks';
};

const itemTypePluralNoun = (type) => (type === 'rock' ? 'missions' : 'tasks');

/** One id shared by every assignee row of a mission so the dashboard can group them. */
const generateMissionId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `m_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeEmail = (email) =>
    String(email || '')
        .trim()
        .toLowerCase();

const CreateTaskModal = ({
    isOpen,
    onClose,
    onTaskCreated,
    currentTeam,
    canCreateTasks = true,
    defaultItemType = 'task'
}) => {
    const [itemType, setItemType] = useState(
        canCreateTasks ? defaultItemType : 'rock'
    );
    const [formData, setFormData] = useState({
        title: '',
        dueDate: '',
        notes: ''
    });
    // Multi-select state: Map of email -> user object
    const [selectedUsers, setSelectedUsers] = useState(new Map());
    // Captain (missions only): email of manually chosen captain; null = use first assignee on submit
    const [captainEmail, setCaptainEmail] = useState(null);

    // UI states
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // For missions (item_type rock), don't scope by currentTeam — allow cross-team assignment
    const effectiveTeam = itemType === 'rock' ? null : currentTeam;

    // Fetch data
    const fetchTeams = async () => {
        if (currentTeam) {
            // Scoped mode: Only show assigned team
            setTeams([{ name: currentTeam }]);
            return;
        }

        try {
            const response = await callOysterApi('/teams', 'GET');
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.payload)) {
                    setTeams(data.payload);
                }
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            let endpoint = '/users/manage?limit=5000';
            if (effectiveTeam) {
                endpoint += `&teamId=${encodeURIComponent(effectiveTeam)}`;
            }

            const response = await callOysterApi(endpoint, 'GET');
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.payload?.users) {
                    const validUsers = data.payload.users
                        .filter((u) => {
                            if (!u.email) return false;
                            if (u.email.toLowerCase().includes('deleted'))
                                return false;
                            if (
                                u.firstName &&
                                u.firstName.toLowerCase() === 'deleteduser'
                            )
                                return false;
                            return true;
                        })
                        .sort((a, b) => {
                            const nameA = `${a.firstName || ''} ${
                                a.lastName || ''
                            }`.trim();
                            const nameB = `${b.firstName || ''} ${
                                b.lastName || ''
                            }`.trim();
                            return nameA.localeCompare(nameB);
                        });
                    setUsers(validUsers);
                }
            } else {
                toast.error('Failed to load users for assignment');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchTeams();
            fetchUsers();
            setSelectedUsers(new Map());
            setCaptainEmail(null);
            setIsDrawerOpen(false);
            setItemType(canCreateTasks ? defaultItemType : 'rock');
        }
    }, [isOpen, currentTeam, canCreateTasks, defaultItemType]);

    // Quarter calculation (mirrors backend getQuarter logic)
    const getQuarter = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return null;
        const year = d.getUTCFullYear();
        const q = Math.floor(d.getUTCMonth() / 3) + 1;
        return `${year}-Q${q}`;
    };

    const quarterLabel = formData.dueDate
        ? getQuarter(formData.dueDate)
        : getQuarter(new Date().toISOString());

    // Re-fetch users when itemType changes (mission = all users, task = scoped)
    useEffect(() => {
        if (isOpen) {
            fetchUsers();
            setSelectedUsers(new Map());
            setCaptainEmail(null);
        }
    }, [itemType]);

    // HANDLERS

    const handleAddTeam = (teamName) => {
        // Leadership includes anyone with isItLeadership, not only hdTeam Leadership
        const teamUsers = usersForTeamBulk(users, teamName);
        if (teamUsers.length === 0) {
            toast.error(`No users found in team ${teamName}`);
            return;
        }

        // Check if all are currently selected
        const allSelected = teamUsers.every((u) => selectedUsers.has(u.email));

        setSelectedUsers((prev) => {
            const next = new Map(prev);
            if (allSelected) {
                // Deselect all
                teamUsers.forEach((u) => next.delete(u.email));
                toast.success(
                    `Removed ${teamUsers.length} users from ${teamName}`
                );
            } else {
                // Select all
                teamUsers.forEach((u) => next.set(u.email, u));
                toast.success(
                    `Added ${teamUsers.length} users from ${teamName}`
                );
            }
            return next;
        });
    };

    const handleAddUser = (userEmail) => {
        const user = users.find((u) => u.email === userEmail);
        if (!user) return;

        setSelectedUsers((prev) => {
            const next = new Map(prev);
            if (next.has(user.email)) {
                next.delete(user.email);
            } else {
                next.set(user.email, user);
            }
            return next;
        });
    };

    const handleRemoveUser = (email) => {
        setSelectedUsers((prev) => {
            const next = new Map(prev);
            next.delete(email);
            return next;
        });
        setCaptainEmail((prev) => (prev === email ? null : prev));
    };

    const handleSetCaptain = (email) => {
        setCaptainEmail((prev) => (prev === email ? null : email));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title) {
            toast.error('Task Title is required');
            return;
        }

        if (selectedUsers.size === 0) {
            toast.error('Please assign at least one user');
            return;
        }

        setSubmitting(true);
        const assignees = Array.from(selectedUsers.values());
        console.log('[CreateTaskModal] Submitting for assignees:', assignees);

        // Captain: explicit ★ pick, else first assignee by default on submit.
        const isMission = itemType === 'rock';
        const missionId = isMission ? generateMissionId() : null;
        const assigneeEmails = assignees.map((u) => u.email);
        let effectiveCaptain = captainEmail;
        if (isMission) {
            if (
                !effectiveCaptain ||
                !assigneeEmails.includes(effectiveCaptain)
            ) {
                effectiveCaptain = assigneeEmails[0] || null;
            }
        }

        let successCount = 0;
        let failCount = 0;

        try {
            const results = await Promise.all(
                assignees.map(async (user) => {
                    if (!user || !user.email) {
                        console.error(
                            '[CreateTaskModal] Invalid user object:',
                            user
                        );
                        return false;
                    }

                    const teamId = user.hdTeam || 'General';
                    const endpoint = `/teams/${encodeURIComponent(
                        teamId
                    )}/todos`;

                    const payload = {
                        title: formData.title,
                        due_date: formData.dueDate,
                        assignee_email: user.email,
                        notes: formData.notes,
                        item_type: itemType,
                        // Mission grouping + Captain (ignored by backend for plain tasks)
                        mission_id: missionId,
                        is_captain:
                            isMission &&
                            normalizeEmail(user.email) ===
                                normalizeEmail(effectiveCaptain)
                    };

                    console.log(
                        '[CreateTaskModal] Sending payload:',
                        payload,
                        'to',
                        endpoint
                    );

                    try {
                        const response = await callOysterApi(
                            endpoint,
                            'POST',
                            payload
                        );
                        return response.ok;
                    } catch (err) {
                        console.error(`Failed to assign to ${user.email}`, err);
                        return false;
                    }
                })
            );

            successCount = results.filter(Boolean).length;
            failCount = results.length - successCount;

            if (successCount > 0) {
                toast.success(
                    `Created ${successCount} ${itemTypeNounForCount(
                        itemType,
                        successCount
                    )} successfully!`
                );
                if (failCount > 0) {
                    toast.error(
                        `${failCount} assignee${
                            failCount === 1 ? '' : 's'
                        } could not be created.`
                    );
                }
                onTaskCreated();
                onClose();
                setFormData({
                    title: '',
                    dueDate: '',
                    notes: ''
                });
                setSelectedUsers(new Map());
                setCaptainEmail(null);
                setItemType(defaultItemType);
            } else {
                toast.error(
                    `Failed to create ${itemTypePluralNoun(
                        itemType
                    )}. Please try again.`
                );
            }
        } catch (error) {
            console.error('Error batch creating task:', error);
            toast.error('Error creating tasks');
        } finally {
            setSubmitting(false);
        }
    };

    // UI Options
    const teamOptions = teams.map((t) => ({
        label: t.name || t,
        value: t.name || t
    }));

    const userOptions = users.map((u) => ({
        label: `${u.firstName} ${u.lastName} (${u.email})`,
        value: u.email
    }));

    const selectedCount = selectedUsers.size;
    const selectedList = Array.from(selectedUsers.values());

    // Highlight Logic
    const selectedUserEmails = new Set(selectedUsers.keys());

    // Determine which teams are "fully assigned" (all their members are selected)
    const fullySelectedTeams = new Set();
    if (users.length > 0 && teams.length > 0) {
        teams.forEach((team) => {
            const teamName = team.name || team;
            const teamUsers = usersForTeamBulk(users, teamName);
            if (teamUsers.length > 0) {
                const allSelected = teamUsers.every((u) =>
                    selectedUsers.has(u.email)
                );
                if (allSelected) {
                    fullySelectedTeams.add(teamName);
                }
            }
        });
    }

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="create-task-overlay">
            <div
                className={`create-task-card create-task-modal ${
                    isDrawerOpen ? 'drawer-open' : ''
                }`}>
                <div className="modal-header">
                    <h2>
                        Create New {itemType === 'rock' ? 'Mission' : 'Task'}
                    </h2>
                    <button className="close-button" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="create-task-form">
                    {/* Mission / Task Toggle */}
                    {canCreateTasks && (
                        <div className="item-type-toggle">
                            <button
                                type="button"
                                className={`toggle-btn ${
                                    itemType === 'rock' ? 'active' : ''
                                }`}
                                onClick={() => setItemType('rock')}>
                                Mission
                            </button>
                            <button
                                type="button"
                                className={`toggle-btn ${
                                    itemType === 'task' ? 'active' : ''
                                }`}
                                onClick={() => setItemType('task')}>
                                Task
                            </button>
                        </div>
                    )}

                    {/* Quarter Preview */}
                    {quarterLabel && (
                        <div className="quarter-preview">
                            Quarter: <strong>{quarterLabel}</strong>
                        </div>
                    )}

                    <div className="form-group">
                        <label>
                            {itemType === 'rock' ? 'Mission' : 'Task'} Title *
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    title: e.target.value
                                })
                            }
                            placeholder="What needs to be done?"
                            className="form-input"
                            autoFocus
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group half">
                            <CustomDropdown
                                label="Add by Team (Bulk)"
                                options={teamOptions}
                                onChange={handleAddTeam} // Just triggering logic, not controlled value
                                placeholder="Select Team to Add..."
                                value="" // Always reset
                                selectedValues={fullySelectedTeams}
                            />
                        </div>
                        <div className="form-group half">
                            <CustomDropdown
                                label="Add Individual"
                                options={userOptions}
                                onChange={handleAddUser}
                                isLoading={loading}
                                placeholder="Search & Select User..."
                                value="" // Always reset
                                selectedValues={selectedUserEmails}
                                keepOpenOnSelect={true}
                            />
                        </div>
                    </div>

                    {/* ASSIGNMENT COUNTER & DRAWER TRIGGER */}
                    <div
                        className="assignment-bar"
                        onClick={() => setIsDrawerOpen(!isDrawerOpen)}>
                        <div className="assign-header-left">
                            <div className="assign-count">
                                Assigning to <strong>{selectedCount}</strong>{' '}
                                user
                                {selectedCount !== 1 ? 's' : ''}
                            </div>
                            {selectedCount > 0 && (
                                <span
                                    className="clear-all-link"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedUsers(new Map());
                                        setCaptainEmail(null);
                                    }}>
                                    (Clear All)
                                </span>
                            )}
                        </div>
                        <div
                            className={`drawer-arrow ${
                                isDrawerOpen ? 'open' : ''
                            }`}>
                            &#9660;
                        </div>
                    </div>

                    {/* EXPANDABLE DRAWER */}
                    {isDrawerOpen && (
                        <div className="assignment-drawer">
                            {itemType === 'rock' && selectedList.length > 0 && (
                                <div className="captain-hint">
                                    Click ★ to set Captain. If none is chosen,
                                    the first assignee becomes Captain.
                                </div>
                            )}
                            {selectedList.length === 0 ? (
                                <div className="empty-drawer">
                                    No users selected yet.
                                </div>
                            ) : (
                                <ul className="selected-users-list">
                                    {selectedList.map((user) => (
                                        <li
                                            key={user.email}
                                            className="selected-user-item">
                                            <div className="user-info">
                                                <span className="user-name">
                                                    {user.firstName}{' '}
                                                    {user.lastName}
                                                    {itemType === 'rock' &&
                                                        captainEmail ===
                                                            user.email && (
                                                            <span className="captain-tag">
                                                                {' '}
                                                                ★ Captain
                                                            </span>
                                                        )}
                                                </span>
                                                <span className="user-team">
                                                    {user.hdTeam || 'No Team'}
                                                </span>
                                            </div>
                                            <div className="user-actions">
                                                {itemType === 'rock' && (
                                                    <button
                                                        type="button"
                                                        className={`captain-toggle-btn ${
                                                            captainEmail ===
                                                            user.email
                                                                ? 'active'
                                                                : ''
                                                        }`}
                                                        title={
                                                            captainEmail ===
                                                            user.email
                                                                ? 'Captain (click to unset)'
                                                                : 'Set as Captain'
                                                        }
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSetCaptain(
                                                                user.email
                                                            );
                                                        }}>
                                                        ★
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    className="remove-user-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveUser(
                                                            user.email
                                                        );
                                                    }}>
                                                    &times;
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label>Due Date</label>
                        <div className="date-input-wrapper">
                            <input
                                type="date"
                                value={formData.dueDate}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        dueDate: e.target.value
                                    })
                                }
                                className="form-input"
                            />
                            <div className="date-presets">
                                <button
                                    type="button"
                                    className="date-pill"
                                    onClick={() => {
                                        const d = new Date();
                                        d.setDate(d.getDate() + 1);
                                        setFormData({
                                            ...formData,
                                            dueDate: d
                                                .toISOString()
                                                .split('T')[0]
                                        });
                                    }}>
                                    Tomorrow
                                </button>
                                <button
                                    type="button"
                                    className="date-pill"
                                    onClick={() => {
                                        const d = new Date();
                                        d.setDate(d.getDate() + 7);
                                        setFormData({
                                            ...formData,
                                            dueDate: d
                                                .toISOString()
                                                .split('T')[0]
                                        });
                                    }}>
                                    One Week
                                </button>
                                <button
                                    type="button"
                                    className="date-pill"
                                    onClick={() => {
                                        const d = new Date();
                                        d.setDate(
                                            d.getDate() +
                                                ((1 + 7 - d.getDay()) % 7 || 7)
                                        );
                                        setFormData({
                                            ...formData,
                                            dueDate: d
                                                .toISOString()
                                                .split('T')[0]
                                        });
                                    }}>
                                    Next Mon
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value
                                })
                            }
                            placeholder="Add details..."
                            className="form-textarea"
                            rows={3}
                        />
                    </div>

                    <div className="modal-actions">
                        <button
                            type="button"
                            className="btn-cancel"
                            onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn-create"
                            disabled={submitting || selectedCount === 0}>
                            {submitting ? (
                                <>
                                    <FaSpinner className="spinner" /> Creating{' '}
                                    {selectedCount}{' '}
                                    {itemType === 'rock' ? 'Mission' : 'Task'}
                                    {selectedCount > 1 ? 's' : ''}...
                                </>
                            ) : (
                                `Create ${
                                    selectedCount > 0 ? selectedCount : ''
                                } ${itemType === 'rock' ? 'Mission' : 'Task'}${
                                    selectedCount > 1 ? 's' : ''
                                }`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

CreateTaskModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onTaskCreated: PropTypes.func.isRequired,
    currentTeam: PropTypes.string,
    canCreateTasks: PropTypes.bool,
    defaultItemType: PropTypes.oneOf(['task', 'rock'])
};

export default CreateTaskModal;
